import globalWindow from '@shared/core/globals';
import { getLocalStorage } from '@shared/core/storage';
import type { ExperienceDefinition, ExperienceVersion } from './ExperienceSwitch.types';
import type { ExperienceId } from './experiences.constants';
import { getExperienceDefinition } from './experiences.constants';

/**
 * The one list of what an `ExperienceVersion` can be.
 *
 * Exported because narrowing to a *subset* of the union still typechecks: a second hand-rolled
 * `value === 'next' || value === 'classic'` elsewhere keeps compiling if the union ever gains a
 * third member, and silently rejects the value this store accepts — with no type error at either
 * site and a symptom (a draft opening in the wrong editor) that reads as a routing bug.
 */
export const isCanonicalVersion = (value: unknown): value is ExperienceVersion => value === 'classic' || value === 'next';

export type ExperienceStore = Readonly<{
  getVersion: () => ExperienceVersion;
  getHasSeenNext: () => boolean;
  setVersion: (version: ExperienceVersion) => void;
  markNextSeen: () => void;
  subscribe: (listener: () => void) => () => void;
}>;

/**
 * A module-level external store for one experience's version preference and seen-flag. Shared rather
 * than per-component so that marking the new build seen on the owning page immediately updates
 * sibling surfaces (a tab-nav "New" badge, a dashboard product row) without waiting for a remount.
 *
 * React is ^17, so there is no `useSyncExternalStore` — the subscribe/notify wiring is by hand.
 * Exported for specs; app code goes through `getExperienceStore`, which shares one store per id.
 */
export const createExperienceStore = (definition: ExperienceDefinition): ExperienceStore => {
  const { storageKey, seenStorageKey, defaultVersion, legacyVersionValues } = definition;

  // getLocalStorage() falls back localStorage → cookie → memory, so a Safari-private-mode admin
  // still gets a preference that sticks for the session instead of one that silently never saves.
  // Note the `storage` event below only fires for real localStorage: under the cookie/memory
  // fallback the preference works but stops syncing across tabs. That is an accepted trade —
  // do not "fix" it by reaching for raw window.localStorage, which loses the fallback entirely.
  const toVersion = (raw: string | null | undefined): ExperienceVersion => {
    if (isCanonicalVersion(raw)) return raw;
    // Re-validate the mapped value: this is the one path into ExperienceVersion that would otherwise
    // skip the guard, so a typo in an adopter's legacy map (or an inherited Object.prototype key like
    // 'toString', which a bare truthiness check would let through as a Function) reaches state unchecked.
    const mappedLegacyValue = raw ? legacyVersionValues?.[raw] : undefined;
    return isCanonicalVersion(mappedLegacyValue) ? mappedLegacyValue : defaultVersion;
  };

  const readVersion = (): ExperienceVersion => toVersion(getLocalStorage().getItem(storageKey));
  const readSeen = (): boolean => getLocalStorage().getItem(seenStorageKey) === 'true';

  let currentVersion: ExperienceVersion | null = null;
  let currentSeen: boolean | null = null;
  const listeners = new Set<() => void>();

  const getVersion = (): ExperienceVersion => {
    if (currentVersion === null) currentVersion = readVersion();
    return currentVersion;
  };

  const getHasSeenNext = (): boolean => {
    if (currentSeen === null) currentSeen = readSeen();
    return currentSeen;
  };

  const notify = () => listeners.forEach(listener => listener());

  const setVersion = (version: ExperienceVersion) => {
    currentVersion = version;
    // Always the canonical union, never a legacy alias — this is what upgrades a stored legacy value.
    getLocalStorage().setItem(storageKey, version);
    notify();
  };

  // Idempotent: once seen, this is a no-op so repeat visits don't re-write storage or fire redundant
  // notifies.
  const markNextSeen = () => {
    if (getHasSeenNext()) return;
    currentSeen = true;
    getLocalStorage().setItem(seenStorageKey, 'true');
    notify();
  };

  // Cross-tab: a `storage` event fires in *other* tabs when localStorage changes here. Re-read and
  // notify so a second open tab's view + pill follow along. Same-tab updates never raise this event;
  // those go through `notify` in the setters instead.
  const handleStorage = (event: StorageEvent) => {
    if (event.key === storageKey) {
      const next = toVersion(event.newValue);
      // Compare against the resolved snapshot, not the raw cache: before this store's first read the
      // cache is null, and a null comparison would notify subscribers for a no-op change.
      if (next === getVersion()) return;
      currentVersion = next;
      notify();
    } else if (event.key === seenStorageKey) {
      const next = event.newValue === 'true';
      if (next === getHasSeenNext()) return;
      currentSeen = next;
      notify();
    }
  };

  const subscribe = (listener: () => void): (() => void) => {
    if (listeners.size === 0) {
      globalWindow.addEventListener?.('storage', handleStorage);
    }
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        globalWindow.removeEventListener?.('storage', handleStorage);
      }
    };
  };

  return { getVersion, getHasSeenNext, setVersion, markNextSeen, subscribe };
};

const stores = new Map<ExperienceId, ExperienceStore>();

/**
 * The one store for an experience, shared by every surface that reads it. Keyed by id so a page and
 * a remote nav badge observe the same value without a provider between them.
 */
export const getExperienceStore = (id: ExperienceId): ExperienceStore => {
  const existing = stores.get(id);
  if (existing) return existing;

  const store = createExperienceStore(getExperienceDefinition(id));
  stores.set(id, store);
  return store;
};
